import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import FormateurPageShell from '../formateur/FormateurPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchAmbassadeurDashboard } from '../../services/academiaAmbassadeurService';
import { formatMoney } from '../../utils/currency';

const QUICK_LINKS = [
  { to: '/ambassadeur/parrainage', icon: 'share', title: 'Partager mon code', desc: 'Lien et QR code de parrainage' },
  { to: '/ambassadeur/wallet', icon: 'account_balance_wallet', title: 'Mon portefeuille', desc: 'Solde et retraits' },
  { to: '/ambassadeur/profil', icon: 'person', title: 'Mon profil', desc: 'Photo et présentation' },
] as const;

type Dashboard = {
  codeParrainage: string;
  registerUrl: string;
  nombreFilleuls: number;
  commissionsTotal: number;
  soldeWallet: number;
  zonePromotion?: string | null;
};

type AmbassadeurUser = {
  telephone?: string;
  mustCompleteProfile?: boolean;
  mustChangePassword?: boolean;
  profil?: { prenom?: string; nom?: string };
};

function ambassadorDisplayName(user?: AmbassadeurUser) {
  return [user?.profil?.prenom, user?.profil?.nom].filter(Boolean).join(' ') || user?.telephone || 'Ambassadeur';
}

function DashboardWidget({
  id,
  title,
  icon,
  linkTo,
  linkLabel,
  children,
}: {
  id: string;
  title: string;
  icon: string;
  linkTo?: string;
  linkLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className="formateur-dash-widget" aria-labelledby={id}>
      <header className="formateur-dash-widget__header">
        <span className="formateur-dash-widget__header-icon" aria-hidden>
          <LucideSymbolIcon name={icon} tone="inherit" size={20} />
        </span>
        <h2 id={id} className="formateur-dash-widget__title">
          {title}
        </h2>
        {linkTo && linkLabel ? (
          <Link to={linkTo} className="formateur-dash-widget__link">
            {linkLabel}
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={14} />
          </Link>
        ) : null}
      </header>
      <div className="formateur-dash-widget__body">{children}</div>
    </section>
  );
}

type Props = {
  user?: AmbassadeurUser;
};

export default function AmbassadorDashboardPanel({ user }: Props) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchAmbassadeurDashboard());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const todoCount =
    (user?.mustCompleteProfile ? 1 : 0) + (user?.mustChangePassword ? 1 : 0);

  return (
    <FormateurPageShell
      title="Espace ambassadeur"
      description="Promouvez la plateforme et gagnez des commissions sur les inscriptions parrainées."
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error}
      onRetry={() => void load()}
      sideCard={{
        label: 'Votre code',
        value: loading ? '…' : data?.codeParrainage ?? '—',
        helper:
          todoCount > 0
            ? `${todoCount} action${todoCount > 1 ? 's' : ''} à traiter`
            : data?.zonePromotion?.trim() || ambassadorDisplayName(user),
        sideLinks: [
          { label: 'Parrainage', to: '/ambassadeur/parrainage', icon: 'share' },
          { label: 'Portefeuille', to: '/ambassadeur/wallet', icon: 'account_balance_wallet' },
        ],
      }}
      primaryAction={
        <Link
          to="/ambassadeur/parrainage"
          className="inline-flex rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary"
        >
          Partager mon code
        </Link>
      }
    >
      {user?.mustCompleteProfile ? (
        <div className="apprenant-dashboard-alert">
          <LucideSymbolIcon name="person" tone="primary" size={18} />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-900">Profil à compléter</p>
            <p className="mt-0.5 text-xs text-on-surface-variant">
              Ajoutez votre photo et votre présentation pour activer le partage de votre code.
            </p>
          </div>
          <Link to="/ambassadeur/profil" className="apprenant-dashboard-alert__action">
            Compléter
          </Link>
        </div>
      ) : null}

      <section className="apprenant-dashboard-kpi" aria-label="Indicateurs">
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--primary">
            <LucideSymbolIcon name="groups" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Filleuls</p>
            <p className="apprenant-dashboard-kpi__value">{loading ? '…' : String(data?.nombreFilleuls ?? 0)}</p>
            <p className="apprenant-dashboard-kpi__hint">Inscriptions parrainées</p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--amber">
            <LucideSymbolIcon name="payments" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Commissions</p>
            <p className="apprenant-dashboard-kpi__value apprenant-dashboard-kpi__value--sm">
              {loading ? '…' : formatMoney(data?.commissionsTotal ?? 0, 'CDF')}
            </p>
            <p className="apprenant-dashboard-kpi__hint">Total cumulé</p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--emerald">
            <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Solde wallet</p>
            <p className="apprenant-dashboard-kpi__value apprenant-dashboard-kpi__value--sm">
              {loading ? '…' : formatMoney(data?.soldeWallet ?? 0, 'CDF')}
            </p>
            <p className="apprenant-dashboard-kpi__hint">Disponible pour retrait</p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--sky">
            <LucideSymbolIcon name="qr_code_2" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Code parrainage</p>
            <p className="apprenant-dashboard-kpi__value font-mono text-base">
              {loading ? '…' : data?.codeParrainage ?? '—'}
            </p>
            <p className="apprenant-dashboard-kpi__hint">À partager aux futurs apprenants</p>
          </div>
        </article>
      </section>

      <section aria-labelledby="ambassadeur-quick-links-heading">
        <h2 id="ambassadeur-quick-links-heading" className="apprenant-dashboard-section-title">
          Accès rapide
        </h2>
        <div className="apprenant-dashboard-quick">
          {QUICK_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="apprenant-dashboard-quick__item">
              <span className="apprenant-dashboard-quick__icon">
                <LucideSymbolIcon name={link.icon} tone="primary" size={20} />
              </span>
              <span className="min-w-0">
                <span className="apprenant-dashboard-quick__title">{link.title}</span>
                <span className="apprenant-dashboard-quick__desc">{link.desc}</span>
              </span>
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="shrink-0 opacity-40" />
            </Link>
          ))}
        </div>
      </section>

      <div className="formateur-dash-grid formateur-dash-grid--2">
        <DashboardWidget id="ambassadeur-link-heading" title="Lien d'inscription" icon="link">
          {data?.registerUrl ? (
            <p className="break-all text-sm text-on-surface-variant">{data.registerUrl}</p>
          ) : (
            <p className="formateur-profil-empty-inline">{loading ? 'Chargement…' : 'Lien indisponible.'}</p>
          )}
        </DashboardWidget>

        <DashboardWidget id="ambassadeur-zone-heading" title="Canal de promotion" icon="campaign">
          {data?.zonePromotion?.trim() ? (
            <p className="text-sm font-semibold text-slate-900">{data.zonePromotion}</p>
          ) : (
            <p className="formateur-profil-empty-inline">
              Zone non assignée. Contactez l&apos;administration si besoin.
            </p>
          )}
        </DashboardWidget>
      </div>
    </FormateurPageShell>
  );
}
